import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { PackageItemFormData } from "@src/common/components/PackageDimensionsForm/PackageDimensionsForm";
import {
  applyDefaultsForPackageType,
  effectiveTypeIdsForPackageItems,
  packageItemsChanged,
  PackageTypeDefaultDimensions,
} from "@src/common/utils/packageTypeDefaults";

interface PackageTypeWithDefaults extends PackageTypeDefaultDimensions {
  id: string;
}

interface UseApplyPackageTypeDefaultsArgs {
  packageTypeId: string | undefined | null;
  packageTypes: PackageTypeWithDefaults[] | undefined;
  arePackageTypesLoading: boolean;
  packageItemsFieldName: string;
  onApplied?: () => void;
  /**
   * When false, None on a row is None — shipment type is not a fallback for
   * defaults. Create-order item Type works this way. Edit/quote keep the
   * override ?: shipment inheritance until those surfaces convert.
   */
  inheritShipmentPackageType?: boolean;
  /**
   * Existing Edit rows already represent persisted values. Treat their initial
   * Types as applied so opening the form cannot inject defaults and dirty them.
   * A later Type change still applies that Type's defaults.
   */
  preserveInitialPackageValues?: boolean;
}

/**
 * When package types are ready, fill empty dim and weight fields from each
 * item's effective type. By default that is override ?: shipment type. Pass
 * inheritShipmentPackageType false so None does not inherit.
 * Callers that need edit-submit to send packageItems should pass onApplied to
 * flip packageItemsModified.
 *
 * Filling is triggered by a row's effective type *changing* — a selection, or
 * the types finishing loading. Editing dimensions re-runs this effect but can
 * never re-fill, so clearing a dimension is a decision the user gets to keep.
 *
 * The returned removal callback keeps the previous-type array aligned when a
 * package row is removed.
 */
export function useApplyPackageTypeDefaults({
  packageTypeId,
  packageTypes,
  arePackageTypesLoading,
  packageItemsFieldName,
  onApplied,
  inheritShipmentPackageType = true,
  preserveInitialPackageValues = false,
}: UseApplyPackageTypeDefaultsArgs) {
  const { getValues, setValue } = useFormContext();
  const packageItems = useWatch({
    name: packageItemsFieldName,
  }) as PackageItemFormData[] | undefined;
  const appliedEffectiveTypeIds = useRef<(string | null)[]>(
    preserveInitialPackageValues
      ? effectiveTypeIdsForPackageItems(
          packageItems,
          inheritShipmentPackageType ? packageTypeId : null,
        )
      : [],
  );
  const onPackageItemRemoved = useCallback((index: number) => {
    appliedEffectiveTypeIds.current.splice(index, 1);
  }, []);

  useEffect(() => {
    if (arePackageTypesLoading || !packageTypes) {
      return;
    }
    // The watched array only triggers this effect; it can lag a row removal
    // (the field array lives in a child component), and acting on that stale
    // snapshot would write the removed row back and refill a cleared row.
    const current: PackageItemFormData[] =
      (getValues(packageItemsFieldName) as PackageItemFormData[] | undefined) ??
      [];
    const effectiveTypeIds: (string | null)[] = effectiveTypeIdsForPackageItems(
      current,
      inheritShipmentPackageType ? packageTypeId : null,
    );
    const previous: (string | null)[] = appliedEffectiveTypeIds.current;

    // Only rows whose effective type changed are filled. Editing or clearing a
    // dimension leaves every effective type untouched, so it can never refill.
    const next: PackageItemFormData[] = current.map((pkg, index) =>
      effectiveTypeIds[index] === previous[index]
        ? pkg
        : applyDefaultsForPackageType(
            pkg,
            effectiveTypeIds[index],
            packageTypes,
            previous[index] ?? null,
          ),
    );

    appliedEffectiveTypeIds.current = effectiveTypeIds;
    if (!packageItemsChanged(current, next)) {
      return;
    }
    setValue(packageItemsFieldName, next, { shouldDirty: true });
    onApplied?.();
  }, [
    arePackageTypesLoading,
    packageTypeId,
    packageTypes,
    packageItemsFieldName,
    inheritShipmentPackageType,
    packageItems,
    getValues,
    setValue,
    onApplied,
  ]);

  return { onPackageItemRemoved };
}
